import { useState } from 'react'

import { useDashboardUseCase } from '../../../application/useCases/useDashboard'
import { DashboardData } from '../../../domain/dashboard.types'
import {
  DASHBOARD_CHART_TYPE,
  DASHBOARD_DATA_TYPE_DISPLAYED,
  DASHBOARD_PERIOD,
  GRAPH_VIEW,
} from '../../../domain/enums/dashboard.enum'
import { TIME_INTERVALS } from '../../../domain/enums/timeInterval.enum'
import { useCustomTranslation } from '../../../infrastructure/translation/useCustomTranslation'
import { formatDataToGraphPoints } from '../../components/graph/graph.helpers'
import { GraphData, TUseGraphData } from '../../components/graph/graph.types'

export const useGraphData = (props: TUseGraphData) => {
  const { selectedPlant, periodSelected } = props
  const { t } = useCustomTranslation('common')
  const { getDataProduction, getDataExpectedProduction } = useDashboardUseCase()
  const [expectedProduction, setExpectedProduction] = useState<DashboardData[]>([])
  const [powerGraphPoints, setPowerGraphPoints] = useState<DashboardData[]>([])
  const [energyGraphPoints, setEnergyGraphPoints] = useState<DashboardData[]>([])
  const [loaders, setLoaders] = useState({ energy: false, graph: false })
  const [displayPowerOrEnergy, setDisplayPowerOrEnergy] = useState<DASHBOARD_DATA_TYPE_DISPLAYED>(
    DASHBOARD_DATA_TYPE_DISPLAYED.POWER
  )

  // Fetch production data when displayPowerOrEnergy === DASHBOARD_DATA_TYPE_DISPLAYED.POWER
  const fetchPowerData = async (timelaps: GRAPH_VIEW, dateStart: number, dateEnd: number) => {
    return await getDataProduction(dateStart, dateEnd, timelaps, selectedPlant)
      .then((data) => {
        const dataSorted = data.sort((a, b) => a.epoch - b.epoch) // Sort the data by epoch to avoid hightcharts error #15
        return dataSorted
      })
      .catch((error) => {
        console.error('Error fetching dashboard data:', error)
      })
  }

  // Fetch expected production data
  const fetchExpectedData = async (dateStart: TIME_INTERVALS, dateEnd: TIME_INTERVALS) => {
    await getDataExpectedProduction(dateStart, dateEnd, selectedPlant)
      .then((data) => {
        const dataSorted = data.sort((a, b) => a.epoch - b.epoch) // Sort the data by epoch to avoid  hightcharts error #15
        setExpectedProduction(dataSorted)
      })
      .catch((error) => {
        console.error('Error fetching dashboard data:', error)
      })
  }

  const graphDataByDay = async (dateStart: number, dateEnd: number) => {
    setLoaders((prev) => ({ ...prev, graph: true, energy: true }))
    setPowerGraphPoints([])
    setEnergyGraphPoints([])
    const powerData = await fetchPowerData(GRAPH_VIEW.DAY_POWER, dateStart, dateEnd)
    const energyData = await fetchPowerData(GRAPH_VIEW.DAY_ENERGY, dateStart, dateEnd)
    setPowerGraphPoints(powerData || [])
    setEnergyGraphPoints(energyData || [])
    setLoaders((prev) => ({ ...prev, graph: false, energy: false }))
  }
  const graphDataByWeek = async (dateStart: number, dateEnd: number) => {
    setLoaders((prev) => ({ ...prev, graph: true, energy: true }))
    setPowerGraphPoints([])
    setEnergyGraphPoints([])
    const energyData = await fetchPowerData(GRAPH_VIEW.WEEK, dateStart, dateEnd)
    setPowerGraphPoints(energyData || [])
    setEnergyGraphPoints(energyData || [])
    setLoaders((prev) => ({ ...prev, graph: false, energy: false }))
  }
  const graphDataByMonth = async (dateStart: number, dateEnd: number) => {
    setLoaders((prev) => ({ ...prev, graph: true, energy: true }))
    setPowerGraphPoints([])
    setEnergyGraphPoints([])
    const energyData = await fetchPowerData(GRAPH_VIEW.MONTH, dateStart, dateEnd)
    setPowerGraphPoints(energyData || [])
    setEnergyGraphPoints(energyData || [])
    setLoaders((prev) => ({ ...prev, graph: false, energy: false }))
  }
  const graphDataByYear = async (dateStart: number, dateEnd: number, expectedEndDate: number) => {
    setLoaders((prev) => ({ ...prev, graph: true, energy: true }))
    setPowerGraphPoints([])
    setEnergyGraphPoints([])
    setExpectedProduction([])
    const energyData = await fetchPowerData(GRAPH_VIEW.YEAR, dateStart, dateEnd)
    setPowerGraphPoints(energyData || [])
    setEnergyGraphPoints(energyData || [])
    await fetchExpectedData(dateStart, expectedEndDate)
    setLoaders((prev) => ({ ...prev, graph: false, energy: false }))
  }

  const graphDataBuilder = () => {
    const options: GraphData[] = []
    if (
      periodSelected === DASHBOARD_PERIOD.DAY &&
      displayPowerOrEnergy === DASHBOARD_DATA_TYPE_DISPLAYED.POWER
    ) {
      options.push({
        name: t('power'),
        type: 'spline',
        points: formatDataToGraphPoints(powerGraphPoints),
      })
      return options
    }
    if (periodSelected === DASHBOARD_PERIOD.YEAR) {
      options.push({
        name: t('production'),
        type: 'column',
        points: formatDataToGraphPoints(powerGraphPoints),
      }),
        // Add expected production line only for the yearly
        options.push({
          name: t('expected_production'),
          type: DASHBOARD_CHART_TYPE.COLUMN,
          points: expectedProduction.map((point: { epoch: number; value: number }) => [
            point.epoch,
            point.value,
          ]),
        })
      return options
    }
    options.push({
      name: t('production'),
      type: 'column',
      points:
        displayPowerOrEnergy === DASHBOARD_DATA_TYPE_DISPLAYED.POWER
          ? formatDataToGraphPoints(powerGraphPoints)
          : formatDataToGraphPoints(energyGraphPoints),
    })
    return options
  }

  return {
    energyGraphPoints,
    powerGraphPoints,
    expectedProduction,
    graphDataLoader: loaders,
    displayPowerOrEnergy,
    graphDataByDay,
    graphDataByWeek,
    graphDataByMonth,
    graphDataByYear,
    graphDataBuilder,
    setDisplayPowerOrEnergy,
  }
}
